import { useState, useCallback, useEffect } from 'react';
const STORAGE_KEY = 'landscope_unit_preferences';
const DEFAULT_PREFERENCES = {
    distanceUnit: 'ft',
    areaUnit: 'sqft',
};
// Area units accepted from storage / legacy migration. Kept as string[] (not
// AreaUnit[]) so `.includes(arbitraryString)` typechecks at the validation
// sites. Mirror the AreaUnit union in utils/units.
const PERSISTED_AREA_UNITS = ['sqm', 'sqft', 'acres', 'hectares', 'sqyd', 'sqkm', 'sqmi'];
const PERSISTED_DISTANCE_UNITS = ['m', 'km', 'ft', 'yd', 'mi'];
function loadPreferences() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                distanceUnit: (PERSISTED_DISTANCE_UNITS.includes(parsed.distanceUnit)) ? parsed.distanceUnit : DEFAULT_PREFERENCES.distanceUnit,
                areaUnit: (PERSISTED_AREA_UNITS.includes(parsed.areaUnit)) ? parsed.areaUnit : DEFAULT_PREFERENCES.areaUnit,
            };
        }
    }
    catch ( /* ignore */_a) { /* ignore */ }
    // Migration: read legacy per-tool keys
    const legacyDistance = localStorage.getItem('landscope_measure_unit') || localStorage.getItem('landscope_path_unit');
    const legacyArea = localStorage.getItem('landscope_polygon_unit');
    const prefs = {
        distanceUnit: (legacyDistance === 'm' || legacyDistance === 'ft') ? legacyDistance : DEFAULT_PREFERENCES.distanceUnit,
        areaUnit: (PERSISTED_AREA_UNITS.includes(legacyArea || '')) ? legacyArea : DEFAULT_PREFERENCES.areaUnit,
    };
    // Persist migrated preferences and clean up legacy keys
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    localStorage.removeItem('landscope_measure_unit');
    localStorage.removeItem('landscope_path_unit');
    localStorage.removeItem('landscope_polygon_unit');
    return prefs;
}
function savePreferences(prefs) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}
// Coupled defaults when the user flips the system-wide Imperial/Metric
// switch. Picks ft + acres for imperial and m + hectares for metric — the
// common land-measurement pair in each system. Users can still override
// to km/sqft/sqm via per-tool dialogs.
const SYSTEM_DEFAULTS = {
    imperial: { distanceUnit: 'ft', areaUnit: 'acres' },
    metric: { distanceUnit: 'm', areaUnit: 'hectares' },
};
const inferSystem = (prefs) => {
    // Distance unit is the primary signal — area units like 'acres' are
    // unambiguously imperial; 'hectares' is metric; 'sqft'/'sqm' track
    // distance.
    return prefs.distanceUnit === 'ft' ? 'imperial' : 'metric';
};
// Module-level singleton state + subscriber set so every useUnitPreferences()
// call across the tree reads from and writes to the same prefs. Without
// this, multiple hook instances each hold their own copy of state and
// updates in one (e.g. the control panel toggle) don't propagate to
// others (e.g. the measure tool's HUD).
let modulePrefs = null;
const subscribers = new Set();
const getPrefs = () => {
    if (modulePrefs === null)
        modulePrefs = loadPreferences();
    return modulePrefs;
};
const updatePrefs = (next) => {
    modulePrefs = next;
    savePreferences(next);
    subscribers.forEach(fn => fn());
};
export const useUnitPreferences = () => {
    const [prefs, setPrefs] = useState(getPrefs);
    // Subscribe to in-tab updates from other hook instances
    useEffect(() => {
        const onChange = () => setPrefs(getPrefs());
        subscribers.add(onChange);
        // Catch up to any updates that happened between initial useState and subscription
        setPrefs(getPrefs());
        return () => { subscribers.delete(onChange); };
    }, []);
    // Sync across tabs/instances via storage event (other-tab changes)
    useEffect(() => {
        const handler = (e) => {
            if (e.key === STORAGE_KEY && e.newValue) {
                try {
                    const parsed = JSON.parse(e.newValue);
                    const next = {
                        distanceUnit: (PERSISTED_DISTANCE_UNITS.includes(parsed.distanceUnit)) ? parsed.distanceUnit : DEFAULT_PREFERENCES.distanceUnit,
                        areaUnit: (PERSISTED_AREA_UNITS.includes(parsed.areaUnit)) ? parsed.areaUnit : DEFAULT_PREFERENCES.areaUnit,
                    };
                    modulePrefs = next;
                    subscribers.forEach(fn => fn());
                }
                catch ( /* ignore */_a) { /* ignore */ }
            }
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);
    const setDistanceUnit = useCallback((unit) => {
        updatePrefs({ ...getPrefs(), distanceUnit: unit });
    }, []);
    const setAreaUnit = useCallback((unit) => {
        updatePrefs({ ...getPrefs(), areaUnit: unit });
    }, []);
    const setSystem = useCallback((system) => {
        updatePrefs(SYSTEM_DEFAULTS[system]);
    }, []);
    return {
        distanceUnit: prefs.distanceUnit,
        areaUnit: prefs.areaUnit,
        system: inferSystem(prefs),
        setDistanceUnit,
        setAreaUnit,
        setSystem,
    };
};
